// src/hooks/useSaveToPMS.ts

import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { endpoints } from '@/react-query/endpoints';
import { useSubmitWriteback } from '@/react-query/videa-notes/useSubmitWriteback';
import { useUpdateNotes } from '@/react-query/videa-notes/useUpdateNotes';

import {
  createNoteUpdatePayload,
  getCurrentTemplate,
  getNoteSummaryWithoutBlurb,
} from '../helpers';
import type { EditedFields } from '../types';

interface UseSaveNoteToPMSProps {
  currentOrganizationId: string;
  currentPracticeId: string;
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  selectedNoteId: string | undefined;
  noteContent: string;
  noteWithTemplateData: ClinicalNote | undefined;
  editedFields: EditedFields;
  hasWritebackEnabled: boolean;
}

export const useSaveNoteToPMS = ({
  currentOrganizationId,
  currentPracticeId,
  unifiedAppointmentId,
  standalonePatientVisitId,
  selectedNoteId,
  noteContent,
  noteWithTemplateData,
  editedFields,
  hasWritebackEnabled,
}: UseSaveNoteToPMSProps) => {
  const queryClient = useQueryClient();

  const updateNoteMutation = useUpdateNotes();
  const submitWritebackMutation = useSubmitWriteback();

  const triggerSaveNoteToPMS = useCallback(() => {
    if (!selectedNoteId) return;

    const noteWithoutBlurb = getNoteSummaryWithoutBlurb(
      noteContent,
      hasWritebackEnabled,
    );

    const currentTemplate = getCurrentTemplate(noteWithTemplateData);
    const isTemplateNote = Boolean(
      currentTemplate && currentTemplate.length > 0,
    );
    const fieldsChanged = editedFields !== null && editedFields.length > 0;

    const updatePayload = createNoteUpdatePayload(
      noteWithoutBlurb,
      isTemplateNote,
      fieldsChanged,
      noteWithTemplateData,
      editedFields,
    );

    updateNoteMutation.mutate(
      {
        clinicalNoteId: selectedNoteId,
        updateNotesBody: updatePayload,
      },
      {
        onSuccess: () => {
          submitWritebackMutation.mutate(selectedNoteId, {
            onSuccess: () => {
              // Invalidate the note cache for the specific appointment
              if (unifiedAppointmentId) {
                queryClient.invalidateQueries({
                  queryKey: [
                    'videa-notes',
                    currentOrganizationId,
                    currentPracticeId,
                    unifiedAppointmentId,
                  ],
                });
              }
              if (standalonePatientVisitId) {
                queryClient.invalidateQueries({
                  queryKey: [
                    'videa-notes',
                    currentOrganizationId,
                    currentPracticeId,
                    standalonePatientVisitId,
                  ],
                });
              }
              // Invalidate the note cache for all appointments to ensure correct status is displayed across Ambient table
              queryClient.invalidateQueries([
                endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
              ]);
              queryClient.invalidateQueries([
                endpoints.ambient.voiceNotes
                  .batchNotesByStandalonePatientVisitIds,
              ]);
            },
          });
        },
      },
    );
  }, [
    selectedNoteId,
    noteContent,
    noteWithTemplateData,
    editedFields,
    hasWritebackEnabled,
    updateNoteMutation,
    submitWritebackMutation,
    queryClient,
    currentOrganizationId,
    currentPracticeId,
    unifiedAppointmentId,
    standalonePatientVisitId,
  ]);

  return {
    triggerSaveNoteToPMS,
    isSavingNoteToPMS:
      updateNoteMutation.isLoading || submitWritebackMutation.isLoading,
  };
};
